import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TableFilterItem } from '../types';
import { useAutoSelectBehavior } from './useAutoSelectBehavior';

interface UseColumnHeaderLogicProps {
  filterItems: TableFilterItem[];
  initialSelectedValues?: string[];
  onFilter?: (columnId: string, selectedValues: string[]) => void;
  onSort?: (columnId: string, direction: 'asc' | 'desc') => void;
  onSearchChange?: (term: string) => void;
  columnId: string;
  autoSelect?: boolean; // Enable auto-select behavior (default: false)
}

export const useColumnHeaderLogic = ({
  filterItems,
  initialSelectedValues = [],
  onFilter,
  onSort,
  onSearchChange,
  columnId,
  autoSelect = false, // Default to false for backward compatibility
}: UseColumnHeaderLogicProps) => {
  // Popover state
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Filter state
  const [selectedValues, setSelectedValues] = useState<string[]>(initialSelectedValues);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Sync with initialSelectedValues when it changes (only if auto-select is disabled)
  useEffect(() => {
    if (!autoSelect) {
      setSelectedValues(initialSelectedValues);
    }
  }, [initialSelectedValues, autoSelect]);

  // Auto-select behavior (if enabled)
  useAutoSelectBehavior({
    filterItems,
    selectedValues,
    setSelectedValues,
    initialSelectedValues,
    enabled: autoSelect,
  });

  // Notify parent when search changes (if callback provided)
  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(searchTerm);
    }
  }, [searchTerm, onSearchChange]);

  // Internal search filtering - only runs when consumer doesn't provide onSearchChange
  // This gives consumers choice: handle search externally OR use built-in client-side filtering
  const filteredItems = useMemo(() => {
    // If consumer handles search, don't filter internally
    if (onSearchChange) {
      return filterItems;
    }

    // Consumer doesn't handle search, filter internally by searchTerm
    if (!searchTerm || searchTerm === '') {
      return filterItems;
    }

    return filterItems.filter((item) => {
      // Handle null, undefined, empty or 'null' string labels
      if (!item.label || item.label === 'null' || item.label === 'undefined') {
        return '-'.includes(searchTerm.toLowerCase());
      }
      // Normal string search for non-empty labels
      return String(item.label).toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [filterItems, searchTerm, onSearchChange]);

  // Popover handlers
  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Filter handlers - use filteredItems (respects search)
  const handleSelectAll = useCallback(() => {
    const allIds = filteredItems.map((item) => item.id);
    setSelectedValues(allIds);
  }, [filteredItems]);

  const handleClearAll = useCallback(() => {
    setSelectedValues([]);
  }, []);

  const handleToggleValue = useCallback((value: string) => {
    setSelectedValues((prev) => {
      if (prev.includes(value)) {
        return prev.filter((id) => id !== value);
      }
      return [...prev, value];
    });
  }, []);

  const handleApplyFilter = useCallback(() => {
    onFilter?.(columnId, selectedValues);
    handleClose();
  }, [onFilter, columnId, selectedValues, handleClose]);

  // Sort handler
  const handleApplySort = useCallback(
    (direction: 'asc' | 'desc') => {
      onSort?.(columnId, direction);
      handleClose();
    },
    [onSort, columnId, handleClose]
  );

  // Search handler
  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const isOpen = Boolean(anchorEl);

  return {
    // Popover
    anchorEl,
    isOpen,
    handleOpen,
    handleClose,

    // Filter
    selectedValues,
    setSelectedValues,
    handleSelectAll,
    handleClearAll,
    handleToggleValue,
    handleApplyFilter,

    // Sort
    handleApplySort,

    // Search
    searchTerm,
    handleSearchChange,

    // Filtered items (filtered by search if consumer doesn't handle it)
    filteredItems,
  };
};
